import React from "react";
import { Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { Platform, TouchableOpacity } from "react-native";
import styled, { css, DefaultTheme } from "styled-components/native";
import useScreenOptionsHooks from "../../../../../hooks/useScreenOptionsHooks";
import TooltipComponent, { StatsBreakdown } from "./TooltipContent";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PlanDetails } from "@src/types/user";

type StatsCardConfig = {
  id: string;
  titleKey: string;
  titleDefault: string;
  tooltipKey: string;
  tooltipDefault: string;
  icon: VDBIcons;
  iconBgColor: string;
  iconColor: string;
  value: string | number;
  labelKey: string;
  labelDefault: string;
  action?: {
    condition: boolean;
    onPress: () => void;
    labelKey: string;
    labelDefault: string;
    showIcon?: boolean;
    disabled?: boolean;
  };
  stats: boolean;
  statsData?: StatsBreakdown;
};

type InfoCardsProps = {
  totalUsers: string | number;
  assignedUsers: string | number;
  unassignedUsers: string | number;
  onHeaderButton: (action: string) => void;
  disableManagePlanButton: (b: { action: string }) => boolean;
  statsBreakdown?: StatsBreakdown;
};

const SingleStatsCard = ({
  config,
  currentPlan,
  typography,
  theme,
  t,
  userInfo,
}: {
  config: StatsCardConfig;
  currentPlan: PlanDetails;
  typography: any;
  theme: any;
  t: (key: string, defaultValue: string) => string;
  userInfo: any;
}) => {
  const isEnrolledTrialCondition =
    userInfo.isEnrolledTrial() &&
    currentPlan?.plan?.total_active_users > currentPlan?.plan?.paid_users &&
    config.id === "fullAccess";
  return (
    <StatsCardContainer
      borderColor={isEnrolledTrialCondition ? theme["error-01"] : null}
    >
      <StatsCardHeader>
        <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
          {t(config.titleKey, config.titleDefault)}
        </VDBText>
        <TooltipComponent
          tooltipText={t(config.tooltipKey, config.tooltipDefault)}
          typography={typography}
          theme={theme}
          stats={config.stats}
          statsData={config.statsData}
          t={t}
        />
      </StatsCardHeader>
      <Spacer size={SpaceType.Basic} />
      <StatsCardContent>
        <FlexContainer>
          <StatsIconOuterContainer bgColor={config.iconBgColor}>
            {isEnrolledTrialCondition && (
              <WarningIconContainer>
                <VDBIcon
                  name={VDBIcons.WARNING}
                  size={20}
                  color={theme["error-01"]}
                  variant={IconsVariant.VDBCustom}
                />
              </WarningIconContainer>
            )}

            <StatsIconContainer bgColor={config.iconBgColor}>
              <VDBIcon
                name={config.icon}
                size={24}
                color={config.iconColor}
                variant={IconsVariant.VDBCustom}
              />
            </StatsIconContainer>
          </StatsIconOuterContainer>
          <Spacer size={SpaceType.ExtraSmall} />

          <VDBText type={typography["Large-B"]} color={theme["core-05"]}>
            {config.value}
          </VDBText>
          <VDBText type={typography["H3-R"]} color={theme["core-05"]}>
            {" "}
            {t(config.labelKey, config.labelDefault)}
          </VDBText>
        </FlexContainer>
        {config.action?.condition && (
          <>
            {/* <Spacer size={SpaceType.Extra} /> */}
            <TouchableOpacity
              onPress={config.action.onPress}
              disabled={config.action.disabled}
            >
              <ActionLink disabled={config.action.disabled}>
                <VDBText
                  type={typography["Body-M"]}
                  color={theme["link-static"]}
                >
                  {t(config.action.labelKey, config.action.labelDefault)}
                </VDBText>
                {config.action.showIcon && (
                  <VDBIcon
                    name={VDBIcons.ADD_CIRCLE}
                    size={16}
                    color={theme["link-static"]}
                    variant={IconsVariant.VDBCustom}
                  />
                )}
              </ActionLink>
            </TouchableOpacity>
          </>
        )}
      </StatsCardContent>
    </StatsCardContainer>
  );
};

const InfoCards = ({
  totalUsers,
  assignedUsers,
  unassignedUsers,
  onHeaderButton,
  disableManagePlanButton,
  statsBreakdown,
}: InfoCardsProps) => {
  const { typography, t, theme } = useScreenOptionsHooks();
  const userInfo = useUserInfo();
  const currentPlan = userInfo.user.plan_details;
  const isPaymentPending = userInfo.isActiveFreeTrial()
    ? userInfo.getUpcomingSubscription()?.is_payment_pending ||
      userInfo.getUpcomingSubscription()?.is_purchase_processing
    : userInfo?.user?.plan_details?.plan?.is_payment_pending ||
      userInfo?.user?.plan_details?.plan?.is_purchase_processing;
  const isEnrolledTrialCondition =
    userInfo.isEnrolledTrial() &&
    currentPlan?.plan?.total_active_users > currentPlan?.plan?.paid_users;

  const cardsConfig: StatsCardConfig[] = [
    {
      id: "fullAccess",
      titleKey: "account.userList.fullAccess.text",
      titleDefault: "Full Access",
      tooltipKey: "account.userList.fullAccess.tooltip.text",
      tooltipDefault:
        "Your total includes free user accounts that come with your active VDB services.",
      icon: VDBIcons.TOTAL_USERS,
      iconBgColor: isEnrolledTrialCondition
        ? theme["error-02"]
        : theme["core-03"],
      iconColor: isEnrolledTrialCondition
        ? theme["error-01"]
        : theme["core-05"],
      value: Number(totalUsers) || "0",
      labelKey: "account.userList.totalUsers.text",
      labelDefault: Number(totalUsers) > 1 ? "Total Users" : "Total User",
      action: {
        condition: Platform.OS === "web",
        onPress: () => onHeaderButton("purchase_seats"),
        labelKey: "account.userList.purchase.text",
        labelDefault: "Add user now",
        showIcon: true,
        disabled: disableManagePlanButton({ action: "purchase_seats" }),
      },
      stats: true,
      statsData: statsBreakdown,
    },
    {
      id: "assigned",
      titleKey: "account.userList.assigned.text",
      titleDefault: "Assigned",
      tooltipKey: "account.userList.assigned.tooltip.text",
      tooltipDefault: "The number of users granted full marketplace access.",
      icon: VDBIcons.ASSIGNED_USERS,
      iconBgColor: theme["success-02"],
      iconColor: theme["success-01"],
      value: Number(assignedUsers) || "–",
      labelKey:
        Number(assignedUsers) > 1
          ? "account.userList.users.text"
          : "account.userList.user.text",
      labelDefault: Number(assignedUsers) > 1 ? "Users" : "User",
      stats: false,
    },
    {
      id: "unassigned",
      titleKey: "account.userList.unassigned.text",
      titleDefault: "Unassigned",
      tooltipKey: "account.userList.unassigned.tooltip.text",
      tooltipDefault:
        "The number of user licenses available to assign full marketplace access.",
      icon: VDBIcons.UNASSIGNED_USERS,
      iconBgColor: theme["warn-02"],
      iconColor: theme["warn-01"],
      value: Number(unassignedUsers) || 0,
      labelKey:
        Number(unassignedUsers) > 1
          ? "account.userList.users.text"
          : "account.userList.user.text",
      labelDefault: Number(unassignedUsers) > 1 ? "Users" : "User",
      action: {
        condition: true,
        onPress: () => onHeaderButton("invite_users"),
        labelKey: "account.userList.card.buttons.inviteUsers.text",
        labelDefault: "Assign",
        showIcon: false,
        disabled:
          !userInfo.isActiveFreeTrial() &&
          (isPaymentPending || userInfo.hasNoPlan()) &&
          unassignedUsers === 0,
      },
      stats: false,
    },
  ];

  return (
    <StatsCardsRow>
      {cardsConfig.map((config) => (
        <SingleStatsCard
          key={config.id}
          config={config}
          typography={typography}
          theme={theme}
          t={t}
          currentPlan={currentPlan}
          userInfo={userInfo}
        />
      ))}
    </StatsCardsRow>
  );
};

export default InfoCards;

const StatsCardsRow = styled.View`
  flex-direction: ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isLargeScreen ? "column" : "row"};
  gap: ${SpaceType.Double}px;
`;

const StatsCardContainer = styled.View`
  ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen &&
    css`
      flex: 1;
    `}
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  padding: ${SpaceType.Double}px;
  border-radius: ${SpaceType.Basic}px;
  ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile &&
    css`
      flex: none;
    `}
  ${({ borderColor }: { borderColor: string }) =>
    borderColor &&
    css`
      border-width: 1px;
      border-color: ${borderColor};
    `}
`;

const StatsCardHeader = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.Basic}px;
`;

const StatsCardContent = styled.View`
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const StatsIconOuterContainer = styled.View<{ bgColor: string }>`
  width: 56px;
  height: 56px;
  border-radius: 24px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border: 2px solid ${({ bgColor }: { bgColor: string }) => bgColor};
  justify-content: center;
  align-items: center;
  position: relative;
`;

const WarningIconContainer = styled.View`
  position: absolute;
  top: -3px;
  right: -3px;
  z-index: 1;
`;

const StatsIconContainer = styled.View<{ bgColor: string }>`
  width: 40px;
  height: 40px;
  border-radius: 16px;
  background-color: ${({ bgColor }: { bgColor: string }) => bgColor};
  justify-content: center;
  align-items: center;
  padding: ${SpaceType.Basic}px;
`;

const ActionLink = styled.View<{ disabled?: boolean }>`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.ExtraSmall}px;
  opacity: ${({ disabled }: { disabled?: boolean }) => (disabled ? 0.5 : 1)};
`;

const FlexContainer = styled.View`
  gap: ${SpaceType.Basic}px;
  flex-direction: row;
  align-items: center;
`;
